/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { captureSentryException } from '@/integrations/sentry/client';

export interface MasterBudget {
  id: string;
  user_id: string;
  year: number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface MasterBudgetItem {
  id: string;
  master_budget_id: string;
  category_id: string;
  sub_category_id: string;
  amount: number;
  currency: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Master Budget queries
// ---------------------------------------------------------------------------

export function useMasterBudgets() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['master-budgets', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('master_budgets')
        .select('*')
        .order('year', { ascending: false });
      if (error) throw error;
      return data as MasterBudget[];
    },
    enabled: !!user,
  });
}

export function useMasterBudget(id: string | undefined) {
  return useQuery({
    queryKey: ['master-budgets', id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('master_budgets')
        .select('*')
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as MasterBudget;
    },
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Master Budget mutations
// ---------------------------------------------------------------------------

export function useCreateMasterBudget() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string; year: number }) => {
      const { data, error } = await (supabase as any)
        .from('master_budgets')
        .insert({ ...input, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data as MasterBudget;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-budgets'] });
      toast.success('Master budget created successfully');
    },
    onError: (err: any) => {
      const message = err?.code === '23505'
        ? 'A master budget for that year already exists'
        : (err?.message ?? 'Failed to create master budget');
      toast.error(message);
      captureSentryException(err);
    },
  });
}

export function useUpdateMasterBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string; name: string; description?: string }) => {
      const { data, error } = await (supabase as any)
        .from('master_budgets')
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as MasterBudget;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-budgets'] });
      toast.success('Master budget updated successfully');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to update master budget');
      captureSentryException(err);
    },
  });
}

export function useDeleteMasterBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('master_budgets')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-budgets'] });
      toast.success('Master budget deleted');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to delete master budget');
      captureSentryException(err);
    },
  });
}

export function useCloneMasterBudget() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({
      sourceId,
      name,
      description,
      targetYear,
    }: {
      sourceId: string;
      name: string;
      description?: string;
      targetYear: number;
    }) => {
      // 1. Create the new master budget
      const { data: newBudget, error: budgetError } = await (supabase as any)
        .from('master_budgets')
        .insert({ name, description, year: targetYear, user_id: user!.id })
        .select()
        .single();
      if (budgetError) throw budgetError;

      // 2. Fetch source items
      const { data: sourceItems, error: itemsError } = await (supabase as any)
        .from('master_budget_items')
        .select('category_id, sub_category_id, amount, currency, description')
        .eq('master_budget_id', sourceId);
      if (itemsError) throw itemsError;

      // 3. Batch insert cloned items (if any)
      if (sourceItems && sourceItems.length > 0) {
        const clonedItems = sourceItems.map((item: any) => ({
          ...item,
          master_budget_id: newBudget.id,
        }));
        const { error: cloneError } = await (supabase as any)
          .from('master_budget_items')
          .insert(clonedItems);
        if (cloneError) throw cloneError;
      }

      return newBudget as MasterBudget;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-budgets'] });
      toast.success('Master budget cloned successfully');
    },
    onError: (err: any) => {
      const message = err?.code === '23505'
        ? 'A master budget for that year already exists'
        : (err?.message ?? 'Failed to clone master budget');
      toast.error(message);
      captureSentryException(err);
    },
  });
}

// ---------------------------------------------------------------------------
// Master Budget Item queries & mutations
// ---------------------------------------------------------------------------

export function useMasterBudgetItems(masterBudgetId: string | undefined) {
  return useQuery({
    queryKey: ['master-budget-items', masterBudgetId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('master_budget_items')
        .select('*')
        .eq('master_budget_id', masterBudgetId!)
        .order('created_at');
      if (error) throw error;
      return data as MasterBudgetItem[];
    },
    enabled: !!masterBudgetId,
  });
}

export function useAddMasterBudgetItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (item: Omit<MasterBudgetItem, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await (supabase as any)
        .from('master_budget_items')
        .insert(item)
        .select()
        .single();
      if (error) throw error;
      return data as MasterBudgetItem;
    },
    onSuccess: (_data: any, variables: any) => {
      qc.invalidateQueries({ queryKey: ['master-budget-items', variables.master_budget_id] });
      toast.success('Budget item added');
    },
    onError: (err: any) => {
      const message = err?.code === '23505'
        ? 'That category/sub-category combination already exists in this budget'
        : (err?.message ?? 'Failed to add budget item');
      toast.error(message);
      captureSentryException(err);
    },
  });
}

export function useUpdateMasterBudgetItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      masterBudgetId,
      ...input
    }: {
      id: string;
      masterBudgetId: string;
      amount?: number;
      currency?: string;
      description?: string;
    }) => {
      const { data, error } = await (supabase as any)
        .from('master_budget_items')
        .update({ ...input, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as MasterBudgetItem;
    },
    onSuccess: (_data: any, variables: any) => {
      qc.invalidateQueries({ queryKey: ['master-budget-items', variables.masterBudgetId] });
      toast.success('Budget item updated');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to update budget item');
      captureSentryException(err);
    },
  });
}

export function useDeleteMasterBudgetItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, masterBudgetId }: { id: string; masterBudgetId: string }) => {
      const { error } = await (supabase as any)
        .from('master_budget_items')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return masterBudgetId;
    },
    onSuccess: (_data: any, variables: any) => {
      qc.invalidateQueries({ queryKey: ['master-budget-items', variables.masterBudgetId] });
      toast.success('Budget item removed');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to remove budget item');
      captureSentryException(err);
    },
  });
}
